import React from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * TextField Component
 * 
 * A flexible text input component with label, helper text, and error states.
 * Follows BankHub design system with proper accessibility.
 * 
 * @example
 * ```tsx
 * <TextField 
 *   label="Email" 
 *   type="email" 
 *   placeholder="you@example.com"
 * />
 * <TextField 
 *   label="Amount" 
 *   type="number" 
 *   error="Amount must be positive"
 * />
 * ```
 */

export interface TextFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Label text */
  label?: string;
  /** Helper text displayed below input */
  helperText?: string;
  /** Error message (shows input as invalid) */
  error?: string;
  /** Left icon/element */
  leftIcon?: React.ReactNode;
  /** Right icon/element */
  rightIcon?: React.ReactNode;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Make input full width */
  fullWidth?: boolean;
  /** Container className */
  containerClassName?: string;
}

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      helperText,
      error,
      leftIcon,
      rightIcon,
      size = 'md',
      fullWidth = false,
      containerClassName = '',
      className = '',
      id,
      disabled,
      required,
      ...props
    },
    ref
  ) => {
    // Generate ID if not provided
    const inputId = id || `textfield-${React.useId()}`;

    // Base input styles (using design tokens)
    const baseStyles = [
      'bg-ds-bg-surface border rounded-lg',
      'text-ds-text-primary placeholder:text-ds-text-muted',
      'transition-all duration-200',
      'focus:outline-none focus:ring-2',
      'disabled:bg-ds-bg-muted disabled:cursor-not-allowed disabled:opacity-60',
    ].join(' ');

    // Error/normal state styles (using design tokens)
    const stateStyles = error
      ? [
          'border-ds-status-error-main',
          'focus:border-ds-status-error-main focus:ring-ds-status-error-main/20',
        ].join(' ')
      : [
          'border-ds-border-default',
          'focus:border-ds-primary-500 focus:ring-ds-primary-500/20',
        ].join(' ');

    // Size styles - MUCH MORE padding to prevent icon overlap
    const sizeStyles = {
      sm: leftIcon ? 'pl-12 pr-3 py-1.5 text-sm' : 'px-3 py-1.5 text-sm',    // Increased to pl-12 (48px)
      md: leftIcon ? 'pl-14 pr-3 py-2.5 text-base' : 'px-3 py-2.5 text-base', // Increased to pl-14 (56px)
      lg: leftIcon ? 'pl-16 pr-4 py-3 text-lg' : 'px-4 py-3 text-lg',         // Increased to pl-16 (64px)
    };

    // Right padding adjustment if rightIcon exists - MUCH MORE padding
    const rightPadding = rightIcon
      ? size === 'sm'
        ? 'pr-12'   // Increased to pr-12 (48px)
        : size === 'lg'
        ? 'pr-16'   // Increased to pr-16 (64px)
        : 'pr-14'   // Increased to pr-14 (56px)
      : '';

    // Width styles
    const widthStyles = fullWidth ? 'w-full' : '';

    // Container width
    const containerWidth = fullWidth ? 'w-full' : '';

    // Combine input styles
    const inputStyles = [
      baseStyles,
      stateStyles,
      sizeStyles[size],
      rightPadding,
      widthStyles,
      className,
    ].join(' ');

    // Icon wrapper widths to reserve space consistently
    const leftIconWrapper = {
      sm: 'w-10',  // 40px reserved
      md: 'w-12',  // 48px reserved
      lg: 'w-14',  // 56px reserved
    };

    const rightIconWrapper = {
      sm: 'w-10',
      md: 'w-12',
      lg: 'w-14',
    };

    return (
      <div className={`${containerWidth} ${containerClassName}`}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-ds-text-primary mb-1.5"
          >
            {label}
            {required && <span className="text-ds-status-error-main ml-1">*</span>}
          </label>
        )}

        {/* Input Container */}
        <div className="relative">
          {/* Left Icon - fixed-width slot */}
          {leftIcon && (
            <div
              className={`absolute inset-y-0 left-0 ${leftIconWrapper[size]} text-ds-text-secondary z-10 flex items-center justify-center`}
            >
              {leftIcon}
            </div>
          )}

          {/* Input */}
          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            required={required}
            className={inputStyles}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={
              error
                ? `${inputId}-error`
                : helperText
                ? `${inputId}-helper`
                : undefined
            }
            {...props}
          />

          {/* Right Icon or Error Icon - fixed-width slot */}
          {(rightIcon || error) && (
            <div
              className={`absolute inset-y-0 right-0 ${rightIconWrapper[size]} z-10 flex items-center justify-center ${
                error ? 'text-ds-status-error-main' : 'text-ds-text-secondary'
              }`}
            >
              {error ? <AlertCircle size={size === 'sm' ? 16 : size === 'lg' ? 24 : 20} /> : rightIcon}
            </div>
          )}
        </div>

        {/* Helper Text or Error Message */}
        {(helperText || error) && (
          <p
            id={error ? `${inputId}-error` : `${inputId}-helper`}
            className={`mt-1.5 text-sm ${
              error
                ? 'text-ds-status-error-dark'
                : 'text-ds-text-secondary'
            }`}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

TextField.displayName = 'TextField';

export default TextField;
